import { parseDate } from "@internationalized/date";
import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { getDisplayLocale } from "@ui/lib/format-price";
import { Link } from "@ui/primitives/Link";
import { cx } from "@ui/utils/cx";
import type { ReactNode } from "react";

export interface Feature {
  url: string;
  featured_on: string;
}

export interface HighlightsProps {
  minAge: number | null;
  maxAge: number | null;
  dropOffAvailable: boolean;
  /** The running community's Squiggle feature, when it has one. */
  feature?: Feature | null;
  className?: string;
}

interface Highlight {
  key: string;
  icon: ReactNode;
  title: string;
  body: ReactNode;
}

/**
 * The at-a-glance facts row: supported age range (always, when known) and
 * drop-off availability (only when the program offers it). Two-up on wider
 * screens, stacked on mobile. Sized down on mobile; the top rule only appears on
 * desktop — inside the mobile summary the section-tab underline serves that role.
 */
export function Highlights({ minAge, maxAge, dropOffAvailable, feature, className }: HighlightsProps) {
  const items: Highlight[] = [];

  const ageTitle = ageRangeLabel(minAge, maxAge);
  if (ageTitle) {
    items.push({
      key: "ages",
      icon: <Icon className="size-5 xl:size-6" name="family" size="24" />,
      title: ageTitle,
      body: t("components.views.programs.show.ages_body"),
    });
  }

  if (dropOffAvailable) {
    items.push({
      key: "drop_off",
      icon: <Icon className="size-5 xl:size-6" name="bag" size="24" />,
      title: t("components.views.programs.show.drop_off_title"),
      body: t("components.views.programs.show.drop_off_body"),
    });
  }

  if (feature) {
    items.push({
      key: "featured",
      icon: <Icon className="size-5 xl:size-6" name="featured" size="24" />,
      title: t("components.views.programs.show.featured_title"),
      body: (
        <>
          {t("components.views.programs.show.featured_body", { date: featureMonth(feature.featured_on) })}{" "}
          <Link className="underline" href={feature.url} rel="noopener noreferrer" target="_blank">
            {t("components.views.programs.show.featured_link")}
          </Link>
        </>
      ),
    });
  }

  if (items.length === 0) return null;

  return (
    <dl
      className={cx(
        "grid grid-cols-1 gap-x-6 gap-y-3 border-neutral-200 border-b pb-4 sm:grid-cols-2",
        "md:border-t md:py-6 xl:gap-x-8 xl:gap-y-4",
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.key} className="flex gap-2 xl:gap-3">
          <span aria-hidden className="mt-0.5 shrink-0 text-neutral-900">
            {item.icon}
          </span>
          <div>
            <dt className="font-semibold text-base text-neutral-900 leading-5 xl:text-lg xl:leading-6">{item.title}</dt>
            <dd className="mt-0.5 text-secondary text-sm leading-5 xl:mt-1 xl:text-base xl:leading-6">{item.body}</dd>
          </div>
        </div>
      ))}
    </dl>
  );
}

/**
 * "November 2025". Date-only ISO must go through parseDate — `new Date("2025-11-01")`
 * parses as UTC midnight and renders the previous month west of UTC.
 */
function featureMonth(iso: string): string {
  const date = parseDate(iso).toDate("UTC");
  return new Intl.DateTimeFormat(getDisplayLocale(), { month: "long", timeZone: "UTC", year: "numeric" }).format(date);
}

function ageRangeLabel(minAge: number | null, maxAge: number | null): string | null {
  if (minAge != null && maxAge != null) {
    return t("components.views.programs.show.ages_title", { min: minAge, max: maxAge });
  }
  if (minAge != null) return t("components.views.programs.show.ages_title_min", { min: minAge });
  if (maxAge != null) return t("components.views.programs.show.ages_title_max", { max: maxAge });
  return null;
}
